import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Server, 
  Mail, 
  Database, 
  CreditCard,
  RefreshCw, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  Send,
  XCircle
} from 'lucide-react';

const AdminSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null);

  const API_URL = getBackendUrl();

  useEffect(() => {
    fetchSystemStatus();
  }, []);

  const fetchSystemStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('horeca_token');
      const response = await axios.get(`${API_URL}/api/admin/system/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatus(response.data);
    } catch (err) {
      console.error('Error fetching system status:', err);
      setError(err.response?.data?.detail || 'Kon systeem status niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const token = localStorage.getItem('horeca_token');
      const response = await axios.post(`${API_URL}/api/admin/system/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon test email niet verzenden');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleTestStripe = async () => {
    setTestingStripe(true);
    setStripeTestResult(null);
    try {
      const token = localStorage.getItem('horeca_token');
      const response = await axios.post(`${API_URL}/api/admin/system/test-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeTestResult(response.data);
      toast.success('Stripe verbinding succesvol!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stripe test mislukt');
      setStripeTestResult({ success: false, error: err.response?.data?.detail });
    } finally {
      setTestingStripe(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
              Systeem Instellingen
            </h1>
            <p className="text-[#57534E] mt-1">
              Bekijk en test de configuratie van alle systeem componenten
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchSystemStatus}
            disabled={loading}
            data-testid="refresh-status-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          <p className="text-rose-700">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && !systemStatus && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#C2410C]" />
        </div>
      )}

      {systemStatus && (
        <div className="space-y-6">
          {/* System Status Card */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                Systeem Status
              </CardTitle>
              <CardDescription>
                Bekijk de configuratie status van alle systeem componenten
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {/* Email Status */}
                <div className={`p-4 rounded-xl border ${systemStatus.email.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${systemStatus.email.configured ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1C1917]">Email (SMTP)</p>
                      <p className={`text-sm ${systemStatus.email.configured ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {systemStatus.email.configured ? '✓ Geconfigureerd' : '⚠ Niet geconfigureerd'}
                      </p>
                    </div>
                  </div>
                  {systemStatus.email.configured && (
                    <div className="text-sm text-[#57534E] space-y-1">
                      <p><span className="font-medium">Host:</span> {systemStatus.email.host}</p>
                      <p><span className="font-medium">Port:</span> {systemStatus.email.port}</p>
                      <p><span className="font-medium">Van:</span> {systemStatus.email.from_email}</p>
                    </div>
                  )}
                </div>

                {/* Stripe Status */}
                <div className={`p-4 rounded-xl border ${systemStatus.stripe.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${systemStatus.stripe.configured ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1C1917]">Stripe</p>
                      <p className={`text-sm ${systemStatus.stripe.configured ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {systemStatus.stripe.configured ? '✓ Geconfigureerd' : '⚠ Niet geconfigureerd'}
                      </p>
                    </div>
                  </div>
                  {systemStatus.stripe.configured && (
                    <div className="text-sm text-[#57534E] space-y-1">
                      <p><span className="font-medium">Price ID:</span> {systemStatus.stripe.has_price_id ? '✓ Ingesteld' : '✗ Ontbreekt'}</p>
                      <p><span className="font-medium">Webhook:</span> {systemStatus.stripe.has_webhook_secret ? '✓ Ingesteld' : '✗ Ontbreekt'}</p>
                    </div>
                  )}
                </div>

                {/* Database Status */}
                <div className="p-4 rounded-xl border bg-emerald-50 border-emerald-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-500">
                      <Database className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1C1917]">Database</p>
                      <p className="text-sm text-emerald-700">✓ Verbonden</p>
                    </div>
                  </div>
                  <div className="text-sm text-[#57534E]">
                    <p><span className="font-medium">Naam:</span> {systemStatus.database.name}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test Email Card */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Test
              </CardTitle>
              <CardDescription>
                Verzend een test email om te controleren of de SMTP configuratie correct werkt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div className="flex-1">
                  <p className="font-medium text-[#1C1917]">Test Email Verzenden</p>
                  <p className="text-sm text-[#57534E]">
                    Er wordt een test email verzonden naar: <strong>{user?.email}</strong>
                  </p>
                </div>
                <Button
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !systemStatus?.email?.configured}
                  className="btn-primary"
                  data-testid="send-test-email-btn"
                >
                  {sendingTestEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Verzend Test Email
                    </>
                  )}
                </Button>
              </div>
              {!systemStatus?.email?.configured && (
                <p className="text-sm text-amber-600 mt-3">
                  ⚠ SMTP is niet geconfigureerd. Voeg de SMTP variabelen toe aan je environment om emails te kunnen verzenden.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Test Stripe Card */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Stripe Test
              </CardTitle>
              <CardDescription>
                Test de Stripe API verbinding door het account profiel op te halen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div className="flex-1">
                  <p className="font-medium text-[#1C1917]">Test Stripe Verbinding</p>
                  <p className="text-sm text-[#57534E]">
                    Controleert of de Stripe API keys correct zijn door het account profiel op te halen
                  </p>
                </div>
                <Button
                  onClick={handleTestStripe}
                  disabled={testingStripe || !systemStatus?.stripe?.configured}
                  className="btn-primary"
                  data-testid="test-stripe-btn"
                >
                  {testingStripe ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testen...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Test Stripe
                    </>
                  )}
                </Button>
              </div>

              {!systemStatus?.stripe?.configured && (
                <p className="text-sm text-amber-600 mt-3">
                  ⚠ Stripe is niet geconfigureerd. Voeg STRIPE_SECRET_KEY toe aan je environment variabelen.
                </p>
              )}

              {/* Stripe Test Result */}
              {stripeTestResult && (
                <div className={`mt-4 p-4 rounded-xl border ${stripeTestResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {stripeTestResult.success ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        <p className="font-medium text-emerald-700">Stripe verbinding succesvol!</p>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-rose-500" />
                        <p className="font-medium text-rose-700">Stripe test mislukt</p>
                      </>
                    )}
                  </div>
                  
                  {stripeTestResult.success && stripeTestResult.account && (
                    <div className="text-sm text-[#57534E] space-y-1">
                      <p><span className="font-medium">Account ID:</span> {stripeTestResult.account.id}</p>
                      <p><span className="font-medium">Bedrijfsnaam:</span> {stripeTestResult.account.business_name}</p>
                      <p><span className="font-medium">Land:</span> {stripeTestResult.account.country}</p>
                      <p><span className="font-medium">Valuta:</span> {stripeTestResult.account.default_currency?.toUpperCase()}</p>
                      <p><span className="font-medium">Email:</span> {stripeTestResult.account.email || 'Niet ingesteld'}</p>
                      <div className="flex gap-4 mt-2">
                        <Badge className={stripeTestResult.account.charges_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                          {stripeTestResult.account.charges_enabled ? '✓ Betalingen actief' : '✗ Betalingen niet actief'}
                        </Badge>
                        <Badge className={stripeTestResult.account.payouts_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                          {stripeTestResult.account.payouts_enabled ? '✓ Uitbetalingen actief' : '✗ Uitbetalingen niet actief'}
                        </Badge>
                      </div>
                    </div>
                  )}
                  
                  {!stripeTestResult.success && stripeTestResult.error && (
                    <p className="text-sm text-rose-600">{stripeTestResult.error}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;
